import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, FolderTree, Package, Building2, 
  Link2, FileText, HelpCircle, LogOut, Menu, X, ChevronRight,
  Settings, Sparkles, BarChart3
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const menuItems = [
  { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { name: "Kategoriler", path: "/admin/kategoriler", icon: FolderTree },
  { name: "Tarifeler", path: "/admin/tarifeler", icon: Package },
  { name: "Sağlayıcılar", path: "/admin/saglayicilar", icon: Building2 },
  { name: "Affiliate Linkler", path: "/admin/affiliate-linkler", icon: Link2 },
  { name: "Sayfalar", path: "/admin/sayfalar", icon: FileText },
  { name: "SSS", path: "/admin/sss", icon: HelpCircle },
  { name: "AI SEO", path: "/admin/seo", icon: Sparkles },
  { name: "Google Entegrasyonları", path: "/admin/google-entegrasyonlari", icon: BarChart3 },
];

export default function AdminLayout({ children, title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    toast.success("Çıkış yapıldı");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="admin-mobile-menu"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <span className="font-heading font-bold text-navy-900">Admin Panel</span>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-navy-900 text-white
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="h-16 flex items-center px-6 border-b border-navy-800">
              <Link to="/admin/dashboard" className="flex items-center gap-3">
                <div className="w-8 h-8 bg-electric-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">B</span>
                </div>
                <span className="font-heading font-bold">Admin Panel</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                      ${isActive 
                        ? "bg-electric-500 text-white" 
                        : "text-slate-300 hover:bg-navy-800 hover:text-white"
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`admin-nav-${item.path.split('/').pop()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Footer */}
            <div className="p-3 border-t border-navy-800">
              <Link
                to="/"
                target="_blank"
                className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm"
              >
                <ChevronRight className="w-4 h-4" />
                Siteyi Görüntüle
              </Link>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-navy-800"
                onClick={handleLogout}
                data-testid="admin-logout"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Çıkış Yap
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Page Header */}
          {title && (
            <div className="bg-white border-b border-slate-200 px-6 py-4">
              <h1 className="font-heading text-2xl font-bold text-navy-900">{title}</h1>
            </div>
          )}

          {/* Page Content */}
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
